import { Box } from '@mui/material';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import {
  getAnalyticalDatasetDetailPath,
  getAnalyticalDatasetVersionPath,
} from '@verantos-dev/core-constants';
import {
  ActionMenu,
  type ActionMenuItemProps,
  type IconType,
} from '@verantos-dev/shared-components';

import { useChatPreservingNavigate } from '../hooks/useChatPreservingNavigate';
import type { CreatedAnalyticalDataset } from '../lib/api/useCreateAnalyticalDatasetMutation';
import type { ProjectArtifact } from '../types';
import { CopyAnalyticalDatasetDialog } from './CopyAnalyticalDatasetDialog';
import { DeleteAnalyticalDatasetDialog } from './DeleteAnalyticalDatasetDialog';
import { RenameAnalyticalDatasetDialog } from './RenameAnalyticalDatasetDialog';

interface AnalyticalDatasetRowActionsProps {
  artifact: ProjectArtifact;
}

/**
 * Per-row action menu for analytical-dataset rows on the artifacts list:
 * Rename + Delete are creator-only; Copy is available to anyone (mirrors cohort).
 * Delete is disabled while a generation run is in progress. Owns its own dialogs
 * via local state (the artifacts list has no AD modal context, unlike cohorts) and
 * uses project-page-local dialogs (feature-analytical-datasets can't be imported
 * here — dependency cycle).
 */
export const AnalyticalDatasetRowActions = ({ artifact }: AnalyticalDatasetRowActionsProps) => {
  const intl = useIntl();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useChatPreservingNavigate();
  const {
    user: { userId, auth0Id },
  } = useAuth();
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isCreator = [userId, auth0Id].includes(artifact.owner.id);
  const isProcessing = artifact.status?.toUpperCase() === 'PROCESSING';

  // Land on the new copy once it's created (mirrors the create flow). Prefer the
  // version route; fall back to the profile route if the version id is absent.
  const onCopied = useCallback(
    (profile: CreatedAnalyticalDataset) => {
      const versionId = profile.latestAnalyticalDatasetVersionId;
      navigate(
        versionId
          ? getAnalyticalDatasetVersionPath(
              projectId,
              profile.analyticalDatasetProfileId,
              versionId
            )
          : getAnalyticalDatasetDetailPath(projectId, profile.analyticalDatasetProfileId)
      );
    },
    [navigate, projectId]
  );

  // Rename + Delete are creator-only; Copy is for everyone. Because Copy is always
  // present, the kebab shows on every AD row.
  const menu: ActionMenuItemProps[] = [];
  if (isCreator) {
    menu.push({
      id: `adArtifactActionMenu-rename-${artifact.id}`,
      label: intl.formatMessage({ id: 'analyticalDatasetsList.actionMenu.rename.label' }),
      onClick: () => setIsRenameOpen(true),
      icon: 'rename',
    });
  }
  menu.push({
    id: `adArtifactActionMenu-copy-${artifact.id}`,
    label: intl.formatMessage({ id: 'analyticalDatasetsList.actionMenu.copy.label' }),
    onClick: () => setIsCopyOpen(true),
    icon: 'copy',
  });
  if (isCreator) {
    menu.push({
      id: `adArtifactActionMenu-delete-${artifact.id}`,
      label: intl.formatMessage({ id: 'analyticalDatasetsList.actionMenu.delete.label' }),
      disabled: isProcessing,
      onClick: () => setIsDeleteOpen(true),
      icon: 'delete',
      iconDisabled: 'deleteBlack' as IconType,
      tooltip: isProcessing
        ? intl.formatMessage({ id: 'analyticalDatasetsList.actionMenu.delete.inProgress' })
        : undefined,
    });
  }

  return (
    <Box
      data-testid={`analytical-dataset-row-actions-${artifact.id}`}
      sx={{ display: 'inline-flex' }}
    >
      <ActionMenu
        id={`adArtifactActionMenu-${artifact.id}`}
        menuItems={menu}
        // Wider than the 220px default so the longest label fits on one line.
        menuWidth={264}
        stopClickEventPropagation
      />
      {isCopyOpen && (
        <CopyAnalyticalDatasetDialog
          projectId={projectId}
          analyticalDatasetProfileId={artifact.id}
          sourceName={artifact.name}
          onCopied={onCopied}
          onClose={() => setIsCopyOpen(false)}
        />
      )}
      {isRenameOpen && (
        <RenameAnalyticalDatasetDialog
          projectId={projectId}
          analyticalDatasetProfileId={artifact.id}
          currentName={artifact.name}
          onClose={() => setIsRenameOpen(false)}
        />
      )}
      {isDeleteOpen && (
        <DeleteAnalyticalDatasetDialog
          projectId={projectId}
          analyticalDatasetProfileId={artifact.id}
          name={artifact.name}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </Box>
  );
};
